import { LitElement, html, css, unsafeCSS, property } from "lit-element";

import { reportsApi } from "../../api/reportsApi";
import { seasonsApi } from "../../api/seasonsApi";
import { seriesApi } from "../../api/seriesApi";

import { DropdownModel } from "../../models/components/DropdownModel";
import { SeriesModel } from "../../models/media/SeriesModel";
import { SeasonModel } from "../../models/media/SeasonModel";
import { MatchModel } from "../../models/media/MatchModel";
import { TemplateModel } from "../../models/template/TemplateModel";
import { ReportFilterModel } from "../../models/report/ReportFilterModel";

import ReportType from "../../enums/report-type";

import Dropdown from "../dropdown/dropdown";

import reportSetupStyles from "./report-setup.scss";
import { matchesApi } from "../../api/matchesApi";
import Consts from "../../consts/consts";

export default class ReportSetup extends LitElement {
    @property({ type: Array })
    seriesList: SeriesModel[];

    @property({ type: Array })
    seasons: SeasonModel[];

    @property({ type: Array })
    matchList: MatchModel[];

    @property({ type: Object })
    reportTemplates: TemplateModel[];

    @property({ type: Object })
    reportFilters: ReportFilterModel[];

    @property({ type: Object })
    selectedSeries: SeriesModel;

    @property({ type: Object })
    selectedSeason: SeasonModel;

    @property({ type: Object })
    selectedMatch: MatchModel;

    @property({ type: Object })
    selectedReportTemplate: TemplateModel;

    @property({ type: Object })
    selectedReportFilter: ReportFilterModel;

    private _container: HTMLElement;

    private _mediaUrl = "";
    private _portalUrl = "";
    private _userId = "";
    private _initReport = undefined;

    private _mediaReportsApi: reportsApi;
    private _seasonsApi: seasonsApi;
    private _seriesApi: seriesApi;
    private _matchesApi: matchesApi;

    private _matchesPageNumber: number;
    private _renderSeries: boolean;
    private _renderSeasons: boolean;
    private _renderMatches: boolean;

    constructor(
        container: HTMLElement,
        mediaUrl: string,
        portalUrl: string,
        userId: string,
        initReport: any) {
        super();

        this._container = container;
        this._mediaUrl = mediaUrl;
        this._portalUrl = portalUrl;
        this._userId = userId;
        this._initReport = initReport;

        this._mediaReportsApi = new reportsApi(this._mediaUrl, this._userId);
        this._seasonsApi = new seasonsApi(this._mediaUrl, this._userId);
        this._seriesApi = new seriesApi(this._mediaUrl, this._userId);
        this._matchesApi = new matchesApi(this._mediaUrl, this._userId);

        this.reportFilters = [];
        this.seasons = [];
        this.seriesList = [];
        this.reportTemplates = [];
        this.matchList = [];

        this._matchesPageNumber = 1;
    }

    async firstUpdated() {

        this.showLoading(true, document.querySelector('#setup-loading-container'));

        [this.reportTemplates, this.reportFilters, this.seriesList] = await Promise.all([
            this._mediaReportsApi.GetTemplatesFromMedia(this._userId),
            this._mediaReportsApi.getReportFiltersFromMedia(this._userId),
            this._seriesApi.getSeriesList('', [])
        ])

        this.seriesList.sort((a, b) => this.textSort(a, b, "name"));
        this.reportTemplates.sort((a, b) => this.textSort(a, b, "name"));
        this.reportFilters.sort((a, b) => this.textSort(a, b, "name"));

        this.showLoading(false, document.querySelector('#setup-loading-container'));
    }

    textSort(a, b, property) {
        let returnValue = 0;

        if (a[property] < b[property])
            returnValue = -1;
        else
            returnValue = 1;

        return returnValue;
    }

    showDropdownsBasedOnReportType() {

        switch (this.selectedReportTemplate.reportType) {

            case ReportType.MatchReport:
            case ReportType.TeamMatchReport:
            case ReportType.PlayerMatchReport:
                this._renderSeries = true;
                this._renderSeasons = true;
                this._renderMatches = true;
                break;
            case ReportType.SeasonReport:
            case ReportType.TeamSeasonReport:
            case ReportType.PlayerSeasonReport:
                this._renderSeries = true;
                this._renderSeasons = true;
                this._renderMatches = false;
                this.selectedMatch = null;
                break;
            case ReportType.TeamSeriesReport:
            case ReportType.PlayerSeriesReport:
                this._renderSeries = true;
                this._renderSeasons = false;
                this._renderMatches = false;
                this.selectedMatch = null;
                this.selectedSeason = null;
            default:
                this._renderSeries = false;
                this._renderSeasons = false;
                this._renderMatches = false;
                break;
        }
    }

    getSeriesList() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "Please select a Series", null));
        for (let [i, series] of Object.entries(this.seriesList)) {
            dropdownItems.push(new DropdownModel(series.seriesGuid, series.name, series));
        }

        let selectedValue = this.selectedSeries ? this.selectedSeries.seriesGuid : undefined;

        return new Dropdown(dropdownItems, this.seriesChange, selectedValue, "Series");
    }

    getSeasons() {
        this.seasons.sort((a, b) => this.textSort(a, b, "name"));

        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "Please select a Season", null));
        for (let [i, season] of Object.entries(this.seasons)) {
            dropdownItems.push(new DropdownModel(season.seasonGuid, season.seasonName, season));
        }

        let selectedValue = this.selectedSeason ? this.selectedSeason.seasonGuid : undefined;

        return new Dropdown(dropdownItems, this.seasonChange, selectedValue, "Season");
    }

    getTemplates() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "Please select a Report", null));
        for (let [i, reportTemplate] of Object.entries(this.reportTemplates)) {
            dropdownItems.push(new DropdownModel(reportTemplate.reportTemplateId, reportTemplate.name, reportTemplate));
        }

        let selectedValue = this.selectedReportTemplate ? this.selectedReportTemplate.reportTemplateId : undefined;

        return new Dropdown(dropdownItems, this.reportTemplateChange, selectedValue, "Report");
    }

    getMatches() {
        return html`        
            <table class="table table-hover" id="matches-table">
                <thead>
                    <tr>
                        <th></th>
                        <th>#</th>
                        <th>Match</th>
                        <th>Date</th>
                        <th>Round</th>
                        <th>Venue</th>
                    </tr>
                <thead>
                    ${this.matchList.map((match, index) => this.buildMatchRow(index, match))}
                    <tr>
                        <td id="loading-row" colspan="6"></td>
                    </tr>
                    <tr>
                        <td id="load-more-row" colspan="6"><button class="btn btn-primary wide-element" @click=${this.loadMoreMatches}>Load More</button></td>
                    </tr>
                </thead>
            </table>
        `;
    }

    buildMatchRow(index: number, match: MatchModel) {
        return html`
        <tr>
            <td>
                <input
                class="radio"
                type="radio"
                name="select_match_radio"
                value=${match}
                @change=${(event) => this.selectedMatchChange(event.target, match)}/>
            </td>
            <td>${index + 1}</td>
            <td>${match.matchDetails}</td>
            <td>${this.formatDate(match.date)}</td>
            <td>${match.roundName ? `${match.roundName}` : `Round ${match.roundNumber}`}</td>
            <td>${match.venue ? match.venue.name : ''}</td>
        </tr>`;
    }

    loadMoreMatches = async () => {
        this._matchesPageNumber++;

        this.showLoading(true, document.querySelector('#loading-row'));
        let additionalMatches: MatchModel[] = await this._matchesApi.searchPreviousMatches('', '', this.selectedSeason.seasonGuid, '', 30, this._matchesPageNumber, true);

        if (additionalMatches.length > 0) {
            this.matchList = this.matchList.concat(additionalMatches);
            this.matchList.sort((a, b) => this.textSort(b, a, "date"));
        }

        this.searchMatchesTable('');
        this.showLoading(false, document.querySelector('#loading-row'));

    }

    formatDate(date: Date) {
        let d: Date = new Date(date);
        let month = d.getMonth();
        let day = '' + d.getDate();
        let year = d.getFullYear();

        let monthName = Consts.monthNames[month];

        if (day.length < 2)
            day = '0' + day;

        return `${day} ${monthName} ${year}`;
    }

    searchMatchesTable = (value) => {
        let i = 0;
        let table = document.getElementById("matches-table");
        let tr = table.getElementsByTagName("tr");

        for (i = 1; i < tr.length - 2; i++) { //Length - 2 to skip final rows with Load More text and Button
            // Hide the row initially.
            tr[i].style.display = "none";
            let td = tr[i].getElementsByTagName("td");

            for (var j = 0; j < td.length; j++) {
                let cell = tr[i].getElementsByTagName("td")[j];
                if (cell) {
                    if (cell.innerHTML.toUpperCase().trim().indexOf(value.toUpperCase().trim()) > -1) {
                        tr[i].style.display = "";
                        break;
                    }
                }
            }
        }
    }

    getFilters() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "Please select a Report Filter", null));
        for (let [i, reportFilter] of Object.entries(this.reportFilters)) {
            dropdownItems.push(new DropdownModel(reportFilter.reportFilterId, reportFilter.name, reportFilter));
        }

        let selectedValue = this.selectedReportFilter ? this.selectedReportFilter.reportFilterId : undefined;

        return new Dropdown(dropdownItems, this.reportFilterChange, selectedValue, "Report Filter");
    }

    seriesChange = async (value: string, seriesModel: SeriesModel) => {
        this.selectedSeason = undefined;
        this._matchesPageNumber = 1;
        this.matchList = [];
        this.selectedSeries = seriesModel;

        if (seriesModel) {
            this.showLoading(true, document.querySelector('#setup-loading-container'));
            this.seasons = await this._seasonsApi.GetSeasonsForSeries(seriesModel.seriesGuid);
            this.showLoading(false, document.querySelector('#setup-loading-container'));
        }
    }

    seasonChange = async (value: string, seasonModel: SeasonModel) => {
        this._matchesPageNumber = 1;
        this.matchList = [];
        this.selectedSeason = seasonModel;
        if (seasonModel) {
            this.showLoading(true, document.querySelector('#setup-loading-container'));
            this.matchList = await this._matchesApi.searchPreviousMatches('', '', seasonModel.seasonGuid, '', 30, this._matchesPageNumber, true);
            this.matchList.sort((a, b) => this.textSort(b, a, "date"));
            this.showLoading(false, document.querySelector('#setup-loading-container'));
        }
    }

    selectedMatchChange = (value, matchModel: MatchModel) => {
        if (value.checked) {
            this.selectedMatch = matchModel;
        }
    }

    reportTemplateChange = (value: string, reportTemplate: TemplateModel) => {
        this.selectedReportTemplate = reportTemplate;
        this.showDropdownsBasedOnReportType();
    }

    reportFilterChange = (value: string, reportFilterModel: ReportFilterModel) => {
        this.selectedReportFilter = reportFilterModel;
    }

    loadReport = () => {
        document.querySelector('#error-container').innerHTML = '';
        let errorMessage = this.createErrorMessage();

        if (errorMessage) {
            errorMessage = "Please ensure the following are selected:" + errorMessage;
            document.querySelector('#error-container').innerHTML = `<span class="text-danger">${errorMessage}</span>`;
            this.showLoading(false, document.querySelector('#setup-loading-container'));
            return;
        }

        let loader = document.createElement('span');
        loader.innerHTML = 'Generating Report...';

        let showTeamHeader = this.selectedReportTemplate.reportType == ReportType.MatchReport || this.selectedReportTemplate.reportType == ReportType.TeamMatchReport || this.selectedReportTemplate.reportType == ReportType.PlayerMatchReport;

        this._initReport(
            this._mediaUrl,
            this._userId,
            //document.querySelector('#report-container'),
            this._container,
            this.selectedReportTemplate.reportId,
            (this.selectedMatch ? this.selectedMatch.matchGuid : undefined),
            this.selectedReportTemplate.reportTemplateId,
            loader,
            undefined,
            this.selectedReportTemplate.reportType,
            false,
            showTeamHeader,
            (this.selectedSeason ? this.selectedSeason.seasonGuid : undefined),
            false,
            undefined,
            true,
            this.selectedSeries.seriesGuid,
            (this.selectedReportFilter ? this.selectedReportFilter.reportFilterId : ""),
            this.selectedReportTemplate.isMultiTemplate);

        this.buildPortalReportLink();
    }

    showLoading(isLoading: boolean, container: HTMLElement) {
        if (isLoading) {
            container.innerHTML = "Loading...";
        }
        else {
            container.innerHTML = "";
        }
    }

    createErrorMessage() {
        let errorMessage: string = '';

        if (!this.selectedReportTemplate) {
            errorMessage += " a Report Template";
        }

        if (this.selectedReportTemplate) {
            switch (this.selectedReportTemplate.reportType) {
                case ReportType.MatchReport:
                case ReportType.TeamMatchReport:
                case ReportType.PlayerMatchReport:
                    if (!this.selectedSeries) {
                        errorMessage += " a Series;";
                    }
                    if (!this.selectedSeason) {
                        errorMessage += " a Season;";
                    }
                    if (!this.selectedMatch) {
                        errorMessage += " a Match;";
                    }
                    break;
                case ReportType.SeasonReport:
                case ReportType.TeamSeasonReport:
                case ReportType.PlayerSeasonReport:
                    if (!this.selectedSeries) {
                        errorMessage += " a Series;";
                    }
                    if (!this.selectedSeason) {
                        errorMessage += " a Season;";
                    }
                    break;
                case ReportType.TeamSeriesReport:
                case ReportType.PlayerSeriesReport:
                    if (!this.selectedSeries) {
                        errorMessage += " a Series and/or Season;";
                    }
                default:
                    break;
            }
        }

        return errorMessage;
    }

    buildPortalReportLink() {
        let portalUrlLink: string = this._portalUrl + "?";
        portalUrlLink += `clientId=${this._userId}&`;
        portalUrlLink += `reportId=${this.selectedReportTemplate.reportId}&`;
        portalUrlLink += `reportType=${this.selectedReportTemplate.reportType}&`;

        switch (this.selectedReportTemplate.reportType) {
            case ReportType.MatchReport:
            case ReportType.TeamMatchReport:
                portalUrlLink += `matchTemplateId=${this.selectedReportTemplate.reportTemplateId}&`;
                portalUrlLink += `matchSeasonId=${this.selectedMatch.matchGuid}&`;
                break;
            case ReportType.PlayerMatchReport:
                portalUrlLink += `playerTemplateId=${this.selectedReportTemplate.reportTemplateId}&`;
                portalUrlLink += `matchSeasonId=${this.selectedMatch.matchGuid}&`;
                break;
            case ReportType.SeasonReport:
            case ReportType.TeamSeasonReport:
            case ReportType.PlayerSeasonReport:
                portalUrlLink += `matchSeasonId=${this.selectedSeason.seasonGuid}&`;
            case ReportType.TeamSeriesReport:
            case ReportType.PlayerSeriesReport:
                portalUrlLink += `seasonTemplateId=${this.selectedReportTemplate.reportTemplateId}&`;
                break;
            default:
                break;
        }

        if (this.selectedReportFilter)
            portalUrlLink += `reportFilterId=${this.selectedReportFilter.reportFilterId}&`;

        portalUrlLink += `seriesId=${this.selectedSeries.seriesGuid}&`;

        if (this.selectedReportTemplate.isMultiTemplate)
        portalUrlLink += `isMultiTemplate=${this.selectedReportTemplate.isMultiTemplate}&`;

        portalUrlLink += `isClickThrough=true`;

        let reportLinkInput: any = document.getElementById("report-link");
        reportLinkInput.value = portalUrlLink;
    }

    //Copies Report Link to Clipboard
    copyReportLink = () => {
        let element: any = document.getElementById("report-link");
        let value = element.value;

        let tempTextArea = document.createElement("textarea");
        document.body.appendChild(tempTextArea);
        tempTextArea.value = value;
        tempTextArea.select();
        document.execCommand("copy");
        document.body.removeChild(tempTextArea);
    }

    //Opens Report Link in new tab
    openReportLink = () => {
        let element: any = document.getElementById("report-link");
        let value = element.value;

        let win = window.open(value, '_blank');
        win.focus();
    }

    createRenderRoot() {
        return this;
    }

    render() {

        return [
            html`
            <style>
                ${css`${unsafeCSS(reportSetupStyles)}`}                     
            </style>

            <div class="container">                

                <div class="row">  
                    <div class="col">
                        ${this.reportTemplates.length > 0 ? this.getTemplates() : ''}
                    </div>  

                    <div class="col">
                        ${this.reportFilters.length > 0 ? this.getFilters() : ''}
                    </div>                    
                </div>

                <div class="row">
                    <div class="col">
                        ${(this._renderSeries && this.seriesList.length) > 0 ? this.getSeriesList() : ''}
                    </div>  
            
                    <div class="col">
                        ${(this._renderSeasons && this.seasons.length > 0) ? this.getSeasons() : ''}
                    </div>  
                </div> 

                <div class="row">
                    <div class="col">
                        ${(this._renderMatches && this.selectedSeason) ?
                    html`<label>Matches</label>
                            <input class="form-control" 
                                @keyup=${(event) => this.searchMatchesTable(event.target.value)} 
                                placeholder="Search..."/>` : ''}
                    </div>
                </div>

                <div class="row">
                    <div class="col" style="max-height: 400px; overflow-y: auto;">
                        ${(this._renderMatches && this.selectedSeason) ? this.getMatches() : ''}
                    </div>
                </div>

                <div class="row">
                    <div class="col">
                        ${this.seriesList.length > 0 ? html`<button class="btn btn-info wide-element" @click=${this.loadReport}>Load Report</button>` : ''}
                    </div>

                    <div class="col">
                        ${this.seriesList.length > 0 ?
                    html`<div class="input-group wide-element">
                            <input id="report-link" type="text" class="form-control" placeholder="Stratus Portal Report Link">
                                <div class="input-group-append">
                                    <button class="btn btn-outline-primary" type="button" @click=${this.copyReportLink}><i class="fa fa-clipboard"></i> Copy</button>
                                    <button class="btn btn-outline-secondary" type="button" @click=${this.openReportLink}><i class="fa fa-external-link"></i> New Tab</button>
                                </div>
                        </div>` : ''}
                    </div>
                </div>

                <div class="row">
                    <div class="col" id="error-container"></div>
                </div>

                <div class="row">
                    <div class="col loading-container" id="setup-loading-container"></div>
                </div>       

            </div>`
        ];
    }
}

window.customElements.define('stratus-stats-report-setup', ReportSetup as any);